import { useMutation, useQuery } from '@apollo/client';
import { StarBorderOutlined, WorkspacePremiumOutlined } from '@mui/icons-material';
import { Button, Stack, Tooltip } from '@mui/material';
import {
  useConfirmationDialog,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataGridContextProvider } from '../../../components/DataGridCustom';
import { AwardingConfirmationDialogContent } from '@/legacy/components/AwardingConfirmationDialog/AwardingConfirmationDialog';
import { getLineItemAwardingImpact } from '@/legacy/components/AwardingConfirmationDialog/AwardingConfirmationDialog.utils';
import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { useModuleContext } from '~/components/ModuleContextProvider';

import { AwardingDataGrid } from './components/AwardingDataGrid';
import { CONTRACT_DEMAND_AWARDING_QUERY } from './ContractDemandAwardingQuery.gql';
import { useLineItemAssignments } from './ContractDemandAwardingWidget.hooks';
import { CONTRACT_DEMAND_AWARD_SUPPLIERS } from './ContractDemandAwardNegotiationCase.gql';

export const ContractDemandAwardingWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandAwardingWidget' });

  const { data, loading: awardingQueryLoading } = useQuery(CONTRACT_DEMAND_AWARDING_QUERY, {
    variables: {
      id: entityId,
    },
    skip: !entityId,
  });

  const [awardToSuppliers, { loading: awardingMutationLoading }] = useMutation(CONTRACT_DEMAND_AWARD_SUPPLIERS, {
    refetchQueries: [
      {
        query: CONTRACT_DEMAND_AWARDING_QUERY,
        variables: {
          id: entityId,
        },
      },
    ],
  });

  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const { confirm } = useConfirmationDialog();

  const assignmentState = useLineItemAssignments(data?.tesseract.contractDemand);
  const awardingImpact = useMemo(() => {
    return getLineItemAwardingImpact(assignmentState.value, data?.tesseract.contractDemand?.sourcingCases ?? []);
  }, [assignmentState.value, data?.tesseract.contractDemand?.sourcingCases]);

  const submitAwardAssignedLineItemsToSourcingCases = useCallback(async () => {
    if (!data?.tesseract.contractDemand?.id || !assignmentState.value) {
      return;
    }

    try {
      const { errors } = await awardToSuppliers({
        variables: {
          id: data.tesseract.contractDemand.id,
          data: {
            awards: Object.entries(assignmentState.value).map(([lineItemId, sourcingCaseId]) => ({
              lineItemId,
              sourcingCaseId,
            })),
          },
        },
      });
      if (!errors) {
        enqueueSuccessSnackbar(t('actions.award.success'));
      }
    } catch (e) {
      onError(e);
    }
  }, [assignmentState.value, data?.tesseract.contractDemand?.id, onError, t, awardToSuppliers, enqueueSuccessSnackbar]);

  const awardAssignedLineItemsToSourcingCases = useMemo(() => {
    if (awardingImpact.dismissedAwardableLineItemsCount === 0) {
      return () => {
        void submitAwardAssignedLineItemsToSourcingCases();
      };
    }

    return confirm(submitAwardAssignedLineItemsToSourcingCases, {
      title: t('actions.award.confirmation.title'),
      maxWidth: 'sm',
      content: (
        <AwardingConfirmationDialogContent
          assignedAwardableLineItemsCount={awardingImpact.assignedAwardableLineItemsCount}
          dismissedAwardableLineItemsCount={awardingImpact.dismissedAwardableLineItemsCount}
          i18nKeyPrefix="ContractDemandAwardingWidget.actions.award.confirmation"
        />
      ),
      confirm: {
        children: t('actions.award.confirmation.confirm'),
        color: 'primary',
      },
      cancel: {
        children: t('actions.award.confirmation.cancel'),
      },
    });
  }, [awardingImpact, confirm, submitAwardAssignedLineItemsToSourcingCases, t]);

  const loading = awardingQueryLoading || awardingMutationLoading;

  const awardingButton = (
    <Button
      disabled={loading || !assignmentState.isAtLeastOneLineItemAssigned}
      onClick={awardAssignedLineItemsToSourcingCases}
      color="primary"
      variant="contained"
      loading={loading}
      startIcon={<WorkspacePremiumOutlined />}
    >
      {t('actions.award.label')}
    </Button>
  );

  return (
    <Widget hover={false}>
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<StarBorderOutlined fontSize="small" />} />}
        actionsRight={
          assignmentState.hasAssignableQuoteItems ? (
            !assignmentState.isAtLeastOneLineItemAssigned ? (
              <Tooltip title={t('actions.award.disabledTooltip')} arrow>
                <span>{awardingButton}</span>
              </Tooltip>
            ) : (
              awardingButton
            )
          ) : null
        }
      />
      <WidgetContent disablePadding>
        <DataGridContextProvider value={{}}>
          {loading || !data?.tesseract.contractDemand ? (
            <Stack position="relative" sx={{ height: '100px' }}>
              <CircularProgressCentered />
            </Stack>
          ) : (
            <AwardingDataGrid contractDemand={data?.tesseract.contractDemand} lineItemAssignments={assignmentState} />
          )}
        </DataGridContextProvider>
      </WidgetContent>
    </Widget>
  );
};
